// Export dialog: a live thumbnail of exactly what will be written, the numbers
// that matter, shared settings, and one card per format with guidance.
// Manufacturing validations (open area, min web, min hole) land in V2-M4.

import { useEffect, useMemo, useRef } from 'react'
import { useStore } from '../state/store'
import { ValueField, useFieldOps } from './ValueField'
import { FORMAT_INFO, runExport, type ExportFormat } from './exporters'
import { validateResult } from '../core/validate'
import {
  IconClose,
  IconExport,
  IconFile,
  IconGrid,
  IconHatch,
  IconMesh,
  IconSolid,
  IconVector,
  IconWarning,
} from './icons'
import type { GenResult } from '../core/types'

const FORMAT_ICONS: Record<ExportFormat, typeof IconVector> = {
  svg: IconVector,
  dxf: IconHatch,
  stl: IconMesh,
  step: IconSolid,
  stepShapes: IconSolid,
  stepSplit: IconGrid,
  json: IconFile,
}

// Advisory manufacturing thresholds (shown inline, never blocking).
const MIN_HOLE_WARN = 1 // mm
const MIN_WEB_WARN = 1 // mm
const OPEN_AREA_WARN = 60 // %
const STEP_ENTITY_WARN = 150000
const STL_TRIANGLE_WARN = 750000

export function ExportDialog() {
  const doc = useStore((s) => s.doc)
  const result = useStore((s) => s.result)
  const setOverlay = useStore((s) => s.setOverlay)
  const pushToast = useStore((s) => s.pushToast)
  const commit = useStore((s) => s.commit)
  const commitDxfBoundary = (on: boolean) =>
    commit('Toggle DXF boundary layer', (d) => {
      d.export.dxfBoundary = on
    })

  const thicknessOps = useFieldOps('Set thickness', (d, v) => {
    d.export.thickness = v
  })
  const toleranceOps = useFieldOps('Set tolerance', (d, v) => {
    d.export.tolerance = v
  })

  const doExport = (format: ExportFormat) => {
    const name = runExport(format, doc, result)
    if (name) pushToast(`${name} written`, 'ok')
    else pushToast('Nothing generated yet, wait for the preview', 'warn')
  }

  const report = useMemo(() => (result ? validateResult(result) : null), [result])
  const warnings: string[] = []
  if (report) {
    if (report.minHoleWidth !== null && report.minHoleWidth < MIN_HOLE_WARN) {
      warnings.push(
        `Smallest hole is ${report.minHoleWidth.toFixed(2)} mm; many processes cannot cut below ${MIN_HOLE_WARN} mm.`,
      )
    }
    if (report.minWeb !== null && report.minWeb < MIN_WEB_WARN) {
      warnings.push(
        `Thinnest wall between holes is ${report.minWeb.toFixed(2)} mm; below ${MIN_WEB_WARN} mm parts get fragile.`,
      )
    }
    if (report.openAreaPct > OPEN_AREA_WARN) {
      warnings.push(`Open area is ${report.openAreaPct.toFixed(0)}%; the panel may lose stiffness.`)
    }
    if (report.stepEntityEstimate > STEP_ENTITY_WARN) {
      warnings.push(
        'Very dense pattern: the STEP panel may be slow to open in some CAD packages. STEP split helps.',
      )
    }
    if (report.stlTriangleEstimate > STL_TRIANGLE_WARN) {
      warnings.push(
        `Dense mesh (about ${Math.round(report.stlTriangleEstimate / 1000)}k triangles): STL export and slicing will take a moment.`,
      )
    }
  }

  return (
    <div className="dialog export-dialog holo-frame" role="dialog" aria-modal="true" aria-label="Export">
      <div className="dialog-head">
        <IconExport size={16} />
        <h2>Export</h2>
        <button className="quiet icon-btn" onClick={() => setOverlay(null)} aria-label="Close (Esc)">
          <IconClose size={13} />
        </button>
      </div>
      <div className="dialog-body">
        <div className="export-top">
          <PreviewThumb result={result} w={doc.canvas.width} h={doc.canvas.height} />
          <div className="export-stats">
            <Stat k="Panel" v={`${doc.canvas.width} x ${doc.canvas.height} mm`} />
            <Stat k="Thickness" v={`${doc.export.thickness} mm`} />
            <Stat k="Holes" v={report ? String(report.holeCount) : '...'} />
            <Stat k="Open area" v={report ? `${report.openAreaPct.toFixed(1)} %` : '...'} />
            <Stat
              k="Min hole"
              v={report?.minHoleWidth != null ? `${report.minHoleWidth.toFixed(2)} mm` : '...'}
            />
            <Stat
              k="Min web"
              v={
                report?.minWeb != null
                  ? `${report.minWeb.toFixed(2)} mm`
                  : report && report.holeCount > 1
                    ? '> 25 mm'
                    : '...'
              }
            />
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="export-warnings" role="note">
            {warnings.map((w) => (
              <div key={w} className="export-warning">
                <IconWarning size={13} />
                {w}
              </div>
            ))}
          </div>
        )}

        <div className="export-settings">
          <ValueField label="Thickness" value={doc.export.thickness} min={0.2} max={100} unit="mm" compact {...thicknessOps} />
          <ValueField
            label="Tolerance"
            value={doc.export.tolerance}
            min={0.005}
            max={0.5}
            step={0.005}
            unit="mm"
            compact
            {...toleranceOps}
          />
          <span className="hint" style={{ margin: 0, flex: 1 }}>
            One pipeline drives the preview and every file: what you see is what is written.
          </span>
        </div>

        <div className="format-grid">
          {(Object.keys(FORMAT_INFO) as ExportFormat[]).map((f) => {
            const info = FORMAT_INFO[f]
            const Icon = FORMAT_ICONS[f]
            const blocked = info.needsResult && !result
            return (
              <div className="format-card" key={f}>
                <div className="format-head">
                  <Icon size={15} />
                  <span className="format-name">{info.name}</span>
                  <span className="format-ext">{info.ext}</span>
                </div>
                <div className="format-desc">{info.desc}</div>
                {f === 'dxf' && (
                  <label className="mini">
                    <input
                      type="checkbox"
                      checked={doc.export.dxfBoundary}
                      onChange={(e) => {
                        const on = e.target.checked
                        commitDxfBoundary(on)
                      }}
                    />
                    Include boundary layer
                  </label>
                )}
                {f === 'stepSplit' && (
                  <div className="row batch-row">
                    <ValueField
                      label="Cols"
                      compact
                      value={doc.export.splitCols}
                      min={1}
                      max={10}
                      step={1}
                      onCommit={(v) =>
                        commit('Set split columns', (d) => {
                          d.export.splitCols = Math.round(v)
                        })
                      }
                    />
                    <ValueField
                      label="Rows"
                      compact
                      value={doc.export.splitRows}
                      min={1}
                      max={10}
                      step={1}
                      onCommit={(v) =>
                        commit('Set split rows', (d) => {
                          d.export.splitRows = Math.round(v)
                        })
                      }
                    />
                  </div>
                )}
                <button className="primary" disabled={blocked} onClick={() => doExport(f)}>
                  Save {info.ext}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="export-stat">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  )
}

// Exact-outline thumbnail rendered from the generation result, so the dialog
// shows the true export geometry, not a screenshot of the canvas.
function PreviewThumb({ result, w, h }: { result: GenResult | null; w: number; h: number }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const theme = useStore((s) => s.theme)
  const BOX = 220

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const scale = Math.min(BOX / w, BOX / h)
    const cw = Math.max(1, Math.round(w * scale))
    const ch = Math.max(1, Math.round(h * scale))
    const dpr = window.devicePixelRatio || 1
    canvas.width = cw * dpr
    canvas.height = ch * dpr
    canvas.style.width = `${cw}px`
    canvas.style.height = `${ch}px`
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const css = getComputedStyle(document.documentElement)
    const tok = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.fillStyle = tok('--cv-sheet', '#0d1219')
    ctx.fillRect(0, 0, cw, ch)
    if (!result) return
    ctx.scale(scale, scale)
    const path = new Path2D()
    const add = (ring: [number, number][]) => {
      path.moveTo(ring[0][0], ring[0][1])
      for (let i = 1; i < ring.length; i++) path.lineTo(ring[i][0], ring[i][1])
      path.closePath()
    }
    add(result.boundary)
    for (const hole of result.holes) {
      if (hole.circle) {
        path.moveTo(hole.circle.cx + hole.circle.r, hole.circle.cy)
        path.arc(hole.circle.cx, hole.circle.cy, hole.circle.r, 0, Math.PI * 2)
      } else {
        for (const ring of hole.poly) add(ring)
      }
    }
    ctx.fillStyle = tok('--cv-panel', '#2c3644')
    ctx.fill(path, 'evenodd')
    ctx.strokeStyle = tok('--cv-panel-line', '#55647a')
    ctx.lineWidth = 1 / scale
    const bpath = new Path2D()
    add(result.boundary)
    ctx.stroke(bpath)
  }, [result, w, h, theme])

  return (
    <div className="export-preview">
      <canvas ref={ref} aria-label="Export preview" />
    </div>
  )
}
